class QueueManager {
    constructor(apiRoot, context, debug = false, nonce = '') {
        this.apiRoot = apiRoot;
        this.context = context; // { type: 'single', id: 123, sort: 'date', page: 1 }
        this.debug = debug;
        this.nonce = nonce;
        
        // Settings from SaborlistaInfinite
        const settings = window.SaborlistaInfinite || {};
        this.historyTTL = settings.historyTTL || 30;
        this.historyCapacity = settings.historyCapacity || 2000;
        this.ignoreHistory = settings.ignoreHistory || false;
        
        // Для одиночных постов курсор - это ID текущего поста.
        // Для архивов - номер страницы. Начинаем со следующей страницы.
        this.cursor = context.type === 'single' ? context.id : (parseInt(context.page || 1) + 1);
        
        this.queue = [];
        
        /**
         * sessionSeenIds (RAM only)
         * Хранит ID всех постов, которые УЖЕ есть в DOM или в очереди этой страницы.
         * Нужен для исключения дублей в рамках одного бесконечного скролла.
         */
        this.sessionSeenIds = new Set();

        /**
         * persistentHistory (LocalStorage)
         * Хранит ID постов, которые пользователь ДЕЙСТВИТЕЛЬНО прочитал (таймер).
         */
        this.persistentHistory = []; // [{id: 123, ts: 123456789}]
        
        this.loadHistory();
        this.gcHistory();

        this.isFetchingPlan = false;
        this.isFetchingItems = false;

        // Добавляем текущий пост в список увиденных в сессии
        if (context.type === 'single') {
            this.addSessionId(parseInt(context.id));
        }

        this.bufferThreshold = 3;

        this.log('QueueManager initialized. Context:', context);
        this.log('Initial cursor:', this.cursor);

        // Save history on leave
        window.addEventListener('beforeunload', () => this.saveHistory());
    }

    log(...args) {
        if (this.debug) {
            console.log('[QueueManager]', ...args);
        }
    }

    error(...args) {
        if (this.debug) {
            console.error('[QueueManager]', ...args);
        }
    }

    loadHistory() {
        try {
            const stored = localStorage.getItem('sbl_infinite_history');
            if (stored) {
                this.persistentHistory = JSON.parse(stored);
                this.log('Persistent History loaded:', this.persistentHistory.length, 'items');
            }
        } catch (e) {
            this.error('Failed to load history', e);
            this.persistentHistory = [];
        }
    }

    saveHistory() {
        try {
            localStorage.setItem('sbl_infinite_history', JSON.stringify(this.persistentHistory));
            this.log('Persistent History saved to LocalStorage');
        } catch (e) {
            this.error('Failed to save history', e);
        }
    }

    gcHistory() {
        const now = Date.now();
        const ttlMs = this.historyTTL * 24 * 60 * 60 * 1000;
        
        const initialCount = this.persistentHistory.length;
        
        // 1. TTL Clean
        this.persistentHistory = this.persistentHistory.filter(item => (now - item.ts) < ttlMs);
        
        // 2. Capacity Clean
        if (this.persistentHistory.length > this.historyCapacity) {
            this.persistentHistory.sort((a, b) => b.ts - a.ts);
            this.persistentHistory = this.persistentHistory.slice(0, this.historyCapacity);
        }
        
        if (this.persistentHistory.length !== initialCount) {
            this.log('History cleaned. Old count:', initialCount, 'New count:', this.persistentHistory.length);
        }
    }

    /**
     * Помечает пост как технически "увиденный" в этой сессии, чтобы не было дублей на одной странице.
     */
    addSessionId(id) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
            this.sessionSeenIds.add(numericId);
        }
    }

    /**
     * Помечает пост как ДЕЙСТВИТЕЛЬНО прочитанный (сохраняется между визитами).
     * Вызывается из ScrollObserver по таймеру.
     */
    markAsRead(id) {
        const numericId = parseInt(id);
        if (isNaN(numericId)) return;

        // Не сохраняем для таксономий (только для сингл контента)
        if (this.context.type !== 'single') return;

        // Проверяем, нет ли уже в истории
        const exists = this.persistentHistory.some(item => item.id === numericId);
        if (!exists) {
            this.persistentHistory.push({ id: numericId, ts: Date.now() });
            this.log('Post marked as READ (Persistent):', numericId);
            // Можно сохранять сразу, но мы также сохраняем по beforeunload
        }
    }

    /**
     * Проверяет, нужно ли пропустить этот пост.
     * Пропускаем если он уже есть на странице (сессия) 
     * ИЛИ если он уже прочитан ранее (только для синглов).
     */
    shouldSkip(id) {
        const numericId = parseInt(id);
        
        // 1. Всегда пропускаем дубли на текущей странице
        if (this.sessionSeenIds.has(numericId)) return true;

        // 2. Для синглов пропускаем то, что уже прочитано в прошлом (если не включено игнорирование истории)
        if (!this.ignoreHistory && this.context.type === 'single') {
            return this.persistentHistory.some(item => item.id === numericId);
        }

        return false;
    }

    async getNextItem() {
        if (this.queue.length < this.bufferThreshold) {
            this.fetchPlan();
        }

        if (this.queue.length === 0) {
            return null;
        }

        const item = this.queue.shift();
        
        if (!item.html) {
            await this.fetchHtmlForItem(item);
        }

        return item;
    }

    async fetchPlan() {
        if (this.isFetchingPlan) return;
        this.isFetchingPlan = true;

        try {
            const groupName = `[QueueManager] Fetch Plan (Cursor: ${this.cursor})`;
            if (this.debug) console.groupCollapsed(groupName);
            
            const contextForRequest = { ...this.context };
            const response = await fetch(`${this.apiRoot}saborlista/v1/get-plan?cursor=${this.cursor}&context=${JSON.stringify(contextForRequest)}`);
            const plan = await response.json();

            if (!Array.isArray(plan)) {
                this.error('Plan is not an array', plan);
                if (this.debug) console.groupEnd();
                return;
            }

            // Фильтруем дубликаты на клиенте (Smart Filter)
            const newItems = plan.filter(item => !this.shouldSkip(item.id));

            if (newItems.length === 0 && plan.length > 0) {
                this.log('All items in plan already seen/read, advancing cursor and retrying (Collision Loop)...');
                this.advanceCursor(plan);
                this.isFetchingPlan = false;
                if (this.debug) console.groupEnd();
                return this.fetchPlan();
            }

            // Добавляем новые айтемы в очередь
            newItems.forEach(item => {
                this.queue.push(item);
                this.addSessionId(item.id); // Помечаем как занятое в этой сессии
            });

            this.log(`Received ${plan.length} items, added ${newItems.length} unique items to queue`);

            this.advanceCursor(plan);
            if (this.debug) console.groupEnd();

        } catch (e) {
            this.error('Error fetching plan', e);
            if (this.debug && this.isFetchingPlan) console.groupEnd();
        } finally {
            this.isFetchingPlan = false;
        }
    }

    advanceCursor(lastPlan) {
        if (this.context.type === 'single') {
            const lastItem = lastPlan[lastPlan.length - 1];
            if (lastItem) this.cursor = lastItem.id;
        } else {
            this.cursor++;
        }
    }

    async fetchHtmlForItem(item) {
        if (item.html || item.isFetching) return;
        item.isFetching = true;

        const templateMode = this.context.type === 'single' ? 'single' : 'card';

        try {
            const groupName = `[QueueManager] Fetch HTML for Item ${item.id}`;
            if (this.debug) console.groupCollapsed(groupName);

            const response = await fetch(`${this.apiRoot}saborlista/v1/render-items?ids=${item.id}&template_mode=${templateMode}`, {
                headers: {
                    'X-WP-Nonce': this.nonce
                }
            });
            const data = await response.json();

            if (Array.isArray(data) && data.length > 0) {
                const rendered = data[0];
                item.html = rendered.html;
                item.meta = rendered.meta;
                this.log('Success: Meta received, HTML length:', item.html.length);
                document.dispatchEvent(new CustomEvent('saborlista:queue-updated', { detail: { count: this.queue.length } }));
            }
            if (this.debug) console.groupEnd();
        } catch (e) {
            this.error(`Error fetching HTML for item ${item.id}`, e);
            if (this.debug) console.groupEnd();
        } finally {
            item.isFetching = false;
        }
    }

    async fetchHtmlBatch(items) {
        if (!items || items.length === 0) return null;

        const ids = items.map(i => i.id).join(',');
        const templateMode = 'card';
        const batchContext = { 
            ...this.context, 
            page: this.context.type === 'single' ? 1 : (this.cursor - 1) 
        };

        try {
            const groupName = `[QueueManager] Fetch Batch HTML (${items.length} items)`;
            if (this.debug) console.groupCollapsed(groupName);
            
            const headers = {};
            if (this.nonce) {
                headers['X-WP-Nonce'] = this.nonce;
            }

            const url = `${this.apiRoot}saborlista/v1/render-items?ids=${ids}&template_mode=${templateMode}&output_format=html&context=${encodeURIComponent(JSON.stringify(batchContext))}`;
            
            const response = await fetch(url, { headers });
            const data = await response.json();

            this.log('Success:', data.count, 'items merged');
            if (this.debug) console.groupEnd();
            return data;
        } catch (e) {
            this.error('Error fetching batch HTML', e);
            if (this.debug) console.groupEnd();
            return null;
        }
    }
}

window.SaborlistaQueueManager = QueueManager;
